import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Button, CircularProgress, IconButton, Paper, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography } from '@mui/material';
import { Add, Delete } from '@mui/icons-material';
import { SalesInvoiceService } from '../../services/salesInvoice.service';
import {
  SalesInvoice, SalesInvoiceLineCreateDTO, SalesInvoiceEditDTO,
} from '../../interfaces/sales/salesInvoice.interface';
import { CustomerService } from '../../services/customer.service';
import { Customer } from '../../interfaces/sales/customer.interface';
import { AccountService } from '../../services/account.service';
import { Account, Segment } from '../../interfaces/accounting.interface';
import { AccountAutocomplete } from '../common/AccountAutocomplete';
import { formatMoney } from '../../utils/format';
import { leafAccounts } from '../../utils/postingAccounts';
import { SegmentService } from '../../services/segment.service';
import { AuthService } from '../../services/auth.service';
import { useToast } from '../../context/ToastContext';
import ScrollableTable from '../common/ScrollableTable';
import SearchableSelect from '../common/SearchableSelect';
import TaxCodeSelect from '../tax/TaxCodeSelect';
import { hasTaxCodes, previewTax, useTaxCodeLookup } from '../tax/useTaxCodeLookup';
import DocumentCurrencyFields from '../common/DocumentCurrencyFields';
import { catalogueUnitPrice, isForeignCurrency, repriceCatalogueLines } from '../../utils/fcDocuments';
import SalesCatalogueSelect from './SalesCatalogueSelect';   // FW-2A
import { discountError, discountPatch, previewLine, shownDiscountAmount } from '../../utils/salesLineDiscount';   // FW-2A
import ProjectSelect, { projectTagLabel, useCustomerProjectOptions } from '../project/ProjectSelect';   // P1-A tail

// In-state row model used by both branches. quantity/unitPrice/taxRatePct may be
// NaN while a numeric field is being typed/cleared; `key` is a stable client key
// that is never sent to the server. For the restricted (ISSUED) branch the
// existing backend line id is carried in `lineId`.
interface LineRow extends Omit<SalesInvoiceLineCreateDTO, 'quantity' | 'unitPrice' | 'taxRatePct'> {
  key: string;
  lineId?: string;
  challanLinked?: boolean; // W3-O: built from a delivery challan; quantity frozen, line cannot be removed
  quantity: number;   // may be NaN while field is empty
  unitPrice: number;  // may be NaN while field is empty
  taxRatePct: number; // may be NaN while field is empty
  itemLabel?: string; // FW-2A
  catalogueBdtPrice?: number | null; // polish 4: set while the price still comes from the catalogue pick
}

// Helpers that treat NaN as 0 for live-total computation only. Same formulas as
// SalesInvoiceCreate.
const safeLineNet = (row: LineRow): number => {
  return previewLine(row).net;   // FW-2A: net of the line discount (shared preview; the server recomputes)
};
const safeLineTax = (row: LineRow): number => {
  if (row.taxCodeId) return previewTax(safeLineNet(row), Number.isFinite(row.taxRatePct) ? row.taxRatePct : 0);
  const t = Number.isFinite(row.taxRatePct) ? row.taxRatePct : 0;
  return +((safeLineNet(row) * t) / 100).toFixed(4);
};

const SalesInvoiceEdit: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { id } = useParams<{ id: string }>();

  // Stable key counter — bumped each time a new row is created.
  const keyCounter = useRef(0);
  const nextKey = () => String(++keyCounter.current);

  // Loaded invoice (drives which branch renders)
  const [invoice, setInvoice] = useState<SalesInvoice | null>(null);

  // Header state
  const [customerId, setCustomerId] = useState('');
  const [issueDate, setIssueDate] = useState('');
  // ---- B4-C: a foreign-currency draft keeps its currency and rate; its lines are in that currency ----
  const [currency, setCurrency] = useState('BDT');
  const [fxRate, setFxRate] = useState<number | ''>('');
  const [currencyLoaded, setCurrencyLoaded] = useState(false);
  const foreign = isForeignCurrency(currency);
  // ---- end B4-C ----
  const [dueDate, setDueDate] = useState('');
  const [notes, setNotes] = useState('');
  // ---- P1-A tail: header project; editable on a DRAFT only (an issued invoice keeps its tag through edit-repost) ----
  const [projectId, setProjectId] = useState('');
  const projectPick = useCustomerProjectOptions(customerId);
  useEffect(() => {
    // switching the customer drops a project the new customer may not use (the stored tag stays for the same customer)
    if (!invoice || customerId === invoice.customerId) return;
    if (projectPick.loaded && projectId && !projectPick.options.some(o => o.id === projectId)) setProjectId('');
  }, [invoice, customerId, projectPick, projectId]);
  // ---- end P1-A tail ----
  const [editReason, setEditReason] = useState('');
  const [editReasonTouched, setEditReasonTouched] = useState(false);

  // Reference data (only needed for the DRAFT full-edit branch)
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [revenueAccounts, setRevenueAccounts] = useState<Account[]>([]);
  const [segments, setSegments] = useState<Segment[]>([]);

  // Lines — stored as LineRow[] (includes transient key)
  const [lines, setLines] = useState<LineRow[]>([]);

  const [loading, setLoading] = useState(true);
  const taxLookup = useTaxCodeLookup(AuthService.getSelectedInstitution());
  const showTaxCodes = hasTaxCodes(taxLookup);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!id) {
      setLoading(false);
      return;
    }
    const institutionId = AuthService.getSelectedInstitution()!;
    Promise.all([
      SalesInvoiceService.getById(id),
      CustomerService.listByInstitution(institutionId, 0, 200, 'ALL'), // FW-1: keep a now-inactive current customer visible
      AccountService.getAccountsByType('REVENUE'),
      SegmentService.getByInstitution(0, 200),
    ])
      .then(([inv, customerPage, revenueAccts, segmentPage]: [
        SalesInvoice, { content: Customer[] }, Account[], { content?: Segment[] },
      ]) => {
        setInvoice(inv);
        setCustomerId(inv.customerId);
        setIssueDate(inv.issueDate);
        setCurrency(inv.currencyCode ?? 'BDT');
        setFxRate(inv.exchangeRate ?? '');
        setCurrencyLoaded(true);
        setDueDate(inv.dueDate);
        setNotes(inv.notes ?? '');
        setProjectId(inv.projectId ?? '');   // P1-A tail

        setCustomers(customerPage.content);
        setRevenueAccounts(Array.isArray(revenueAccts) ? revenueAccts : []);
        const allSegments: Segment[] = segmentPage.content ?? [];
        setSegments(allSegments.filter(s => s.active !== false));

        // Prefill lines from the invoice. For DRAFT we still carry lineId
        // harmlessly; for ISSUED/PARTIALLY_PAID we need it for the edit DTO.
        setLines((inv.lines ?? []).map(l => ({
          key: nextKey(),
          lineId: l.id,
          challanLinked: !!l.deliveryChallanLineId,
          description: l.description,
          quantity: l.quantity,
          unitPrice: isForeignCurrency(inv.currencyCode) && l.foreignUnitPrice != null ? l.foreignUnitPrice : l.unitPrice,
          taxRatePct: l.taxRatePct,
          taxCodeId: l.taxCodeId ?? null,
          revenueAccountId: l.revenueAccountId || undefined,
          segmentId: l.segmentId || undefined,
          itemId: l.itemId ?? null,   // FW-2A
          itemLabel: l.itemId ? 'Catalogue item' : undefined,
          discountPct: l.discountPct ?? null,
          // FW-2A: a foreign invoice is edited in its document currency, so reload the FOREIGN discount (discountAmount is BDT)
          discountAmount: l.discountPct == null
            && Number((isForeignCurrency(inv.currencyCode) ? l.foreignDiscountAmount : l.discountAmount) ?? 0) > 0
            ? (isForeignCurrency(inv.currencyCode) ? l.foreignDiscountAmount : l.discountAmount) : null,
        })));
      })
      .catch(e => toast.showApiError(e, 'Failed to load invoice'))
      .finally(() => setLoading(false));
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAddLine = () => {
    const dcId = taxLookup?.defaultOutputCodeId;
    const dc = dcId ? taxLookup?.codes?.find(c => c.id === dcId && c.active) : undefined;
    setLines(prev => [...prev, {
      key: nextKey(),
      description: '',
      quantity: 1,
      unitPrice: NaN,
      taxRatePct: dc ? dc.effectiveRatePct : 0,
      taxCodeId: dc ? dc.id : null,
      revenueAccountId: undefined,
      segmentId: undefined,
    }]);
  };

  const handleRemoveLine = (index: number) => {
    setLines(prev => prev.filter((_, i) => i !== index));
  };

  const handleLineChange = <K extends keyof LineRow>(
    index: number,
    field: K,
    value: LineRow[K]
  ) => {
    setLines(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value,
        ...(field === 'unitPrice' ? { catalogueBdtPrice: null } : {}) };   // polish 4: a typed price stops catalogue re-pricing
      return updated;
    });
  };

  // Live totals — NaN fields treated as 0.
  const subtotal = lines.reduce((s, l) => s + safeLineNet(l), 0);
  const taxAmount = lines.reduce((s, l) => s + safeLineTax(l), 0);
  const total = subtotal + taxAmount;

  const status = invoice?.status;
  const isDraft = status === 'DRAFT';
  const isRestricted = status === 'ISSUED' || status === 'PARTIALLY_PAID';

  const draftSubmitDisabled =
    saving ||
    !customerId ||
    lines.length === 0 ||
    lines.some(l => !(l.quantity > 0) || !(l.unitPrice > 0) || !!discountError(l));

  const restrictedSubmitDisabled =
    saving ||
    !editReason.trim() ||
    lines.some(l => !(l.unitPrice > 0) || !!discountError(l));

  // DRAFT → full replacement via update(id, SalesInvoiceCreateDTO)
  const handleDraftSubmit = async () => {
    if (!id) return;
    if (!customerId) { toast.showApiError(null, 'Select a customer'); return; }
    if (lines.length === 0) { toast.showApiError(null, 'Add at least one line'); return; }
    if (lines.some(l => !(l.quantity > 0) || !(l.unitPrice > 0))) {
      toast.showApiError(null, 'All lines must have a positive quantity and unit price');
      return;
    }
    if (foreign && !(Number(fxRate) > 0)) { toast.showApiError(null, `Enter the ${currency} rate`); return; }

    setSaving(true);
    try {
      const institutionId = AuthService.getSelectedInstitution()!;
      const dto = {
        customerId,
        issueDate,
        dueDate,
        notes: notes || undefined,
        institutionId,
        // B4-C: always state the currency so the server never defaults a foreign draft to BDT
        currencyCode: currency,
        ...(foreign ? { exchangeRate: Number(fxRate) } : {}),
        projectId: projectId || null,   // P1-A tail: PUT semantics, an empty pick clears the tag
        lines: lines.map(l => ({
          description: l.description,
          quantity: l.quantity,
          unitPrice: l.unitPrice,
          taxRatePct: Number.isFinite(l.taxRatePct) ? l.taxRatePct : 0,
          taxCodeId: l.taxCodeId || null,
          revenueAccountId: l.revenueAccountId || undefined,
          segmentId: l.segmentId || undefined,
          existingLineId: l.challanLinked ? l.lineId : undefined, // W3-O
          itemId: l.itemId || undefined,   // FW-2A
          discountPct: l.discountPct ?? undefined,
          discountAmount: l.discountPct == null ? (l.discountAmount ?? undefined) : undefined,
        })),
      };
      await SalesInvoiceService.update(id, dto);
      toast.showSuccess('Sales Invoice updated');
      navigate(`/sales/invoices/${id}`);
    } catch (e) {
      toast.showApiError(e, 'Failed to update Sales Invoice');
    } finally {
      setSaving(false);
    }
  };

  // ISSUED/PARTIALLY_PAID → audit-trailed correction via edit(id, SalesInvoiceEditDTO)
  const handleRestrictedSubmit = async () => {
    if (!id) return;
    if (!editReason.trim()) { toast.showApiError(null, 'Edit reason is required'); return; }
    if (lines.some(l => !(l.unitPrice > 0))) {
      toast.showApiError(null, 'All lines must have a positive unit price');
      return;
    }
    if (lines.some(l => !l.lineId)) {
      toast.showApiError(null, 'Internal error: a line is missing its id');
      return;
    }

    setSaving(true);
    try {
      const dto: SalesInvoiceEditDTO = {
        editReason: editReason.trim(),
        issueDate,
        dueDate,
        notes: notes || undefined,
        lines: lines.map(l => ({
          lineId: l.lineId!,
          unitPrice: l.unitPrice,
          taxRatePct: Number.isFinite(l.taxRatePct) ? l.taxRatePct : 0,
          taxCodeId: l.taxCodeId || null,
          description: l.description,
          itemId: l.itemId || undefined,   // FW-2A (kept when blank)
          discountPct: l.discountPct ?? undefined,   // FW-2A: authoritative, none = no discount
          discountAmount: l.discountPct == null && Number(l.discountAmount ?? 0) > 0 ? l.discountAmount ?? undefined : undefined,
        })),
      };
      await SalesInvoiceService.edit(id, dto);
      toast.showSuccess('Sales Invoice updated');
      navigate(`/sales/invoices/${id}`);
    } catch (e) {
      toast.showApiError(e, 'Failed to update Sales Invoice');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Box sx={{ p: 3 }}><CircularProgress /></Box>;

  if (!invoice) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Invoice not found.</Typography>
        <Button data-help="sales.common.back" variant="outlined" onClick={() => navigate(-1)}>Back</Button>
      </Box>
    );
  }

  // PAID or VOIDED → not editable
  if (!isDraft && !isRestricted) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h5" sx={{ mb: 2 }}>
          Edit Sales Invoice {invoice.invoiceNumber ?? ''}
        </Typography>
        <Typography sx={{ mb: 3 }} color="text.secondary">
          This invoice can no longer be edited.
        </Typography>
        <Button data-help="sales.common.back" variant="outlined" onClick={() => navigate(`/sales/invoices/${invoice.id}`)}>
          Back
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }} data-help-page="sales.invoiceEdit.page">
      <Typography variant="h5" sx={{ mb: 3 }}>
        Edit Sales Invoice {invoice.invoiceNumber ?? ''}
        {isRestricted && (
          <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
            (restricted edit — {status})
          </Typography>
        )}
      </Typography>

      {/* Header */}
      <Paper sx={{ p: 3, mb: 3 }} data-help-section="sales.invoiceEdit.section.header">
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {isDraft ? (
            <SearchableSelect data-help="sales.invoiceEdit.field.customer"
              label="Customer"
              required
              options={customers}
              value={customerId}
              onChange={id => setCustomerId(id)}
              getOptionLabel={c => c.name}
            />
          ) : (
            <TextField data-help="sales.invoiceEdit.field.customer"
              label="Customer"
              value={invoice.customerName ?? customerId}
              InputProps={{ readOnly: true }}
              disabled
            />
          )}

          {isDraft ? (
            <ProjectSelect data-help="sales.invoiceEdit.field.project"
              label="Project (optional)"
              data-testid="inv-project"
              value={projectId}
              onChange={pid => setProjectId(pid || '')}
              options={projectPick.options}
              currentLabel={invoice.projectId === projectId ? projectTagLabel(invoice.projectCode, invoice.projectName) : null}
              disabled={!customerId}
            />
          ) : invoice.projectId ? (
            <TextField data-help="sales.invoiceEdit.field.project"
              label="Project"
              value={projectTagLabel(invoice.projectCode, invoice.projectName)}
              InputProps={{ readOnly: true }}
              disabled
            />
          ) : null}

          {isDraft && currencyLoaded && (
            <DocumentCurrencyFields institutionId={AuthService.getSelectedInstitution() ?? ''} currency={currency}
              currencyProps={{ 'data-help': 'sales.invoiceEdit.field.currency' }} rateProps={{ 'data-help': 'sales.invoiceEdit.field.rate' }}
              rate={fxRate} asOf={issueDate} onChange={(c, r) => { setCurrency(c); setFxRate(r); setLines(prev => repriceCatalogueLines(prev, c, r)); }} />
          )}

          <TextField data-help="sales.invoiceEdit.field.issueDate"
            label="Issue Date"
            type="date"
            value={issueDate}
            onChange={e => setIssueDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            required
          />

          <TextField data-help="sales.invoiceEdit.field.dueDate"
            label="Due Date"
            type="date"
            value={dueDate}
            onChange={e => setDueDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            required
          />

          <TextField data-help="sales.invoiceEdit.field.notes"
            label="Notes"
            value={notes}
            onChange={e => setNotes(e.target.value)}
            multiline
            minRows={3}
          />

          {isRestricted && (
            <TextField data-help="sales.invoiceEdit.field.editReason"
              label="Edit Reason"
              value={editReason}
              onChange={e => setEditReason(e.target.value)}
              onBlur={() => setEditReasonTouched(true)}
              required
              error={editReasonTouched && !editReason.trim()}
              helperText="Required — recorded in the invoice audit trail"
              multiline
              minRows={2}
            />
          )}
        </Box>
      </Paper>

      {/* Lines */}
      <Paper sx={{ p: 3, mb: 3 }} data-help-section="sales.invoiceEdit.lines">
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="h6">Invoice Lines</Typography>
          {isDraft && (
            <Button variant="outlined" data-help="sales.invoiceEdit.addLine" startIcon={<Add />} onClick={handleAddLine}>
              Add Line
            </Button>
          )}
        </Box>

        {lines.length === 0 ? (
          <Typography color="text.secondary">No lines.</Typography>
        ) : (
          <ScrollableTable>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {isDraft && <TableCell data-help="sales.invoiceEdit.col.product">Product / service</TableCell>}
                    <TableCell data-help="sales.invoiceEdit.col.description">Description</TableCell>
                    <TableCell data-help="sales.invoiceEdit.col.qty">Quantity</TableCell>
                    <TableCell data-help="sales.invoiceEdit.col.unitPrice">{foreign && isDraft ? `Unit Price (${currency})` : 'Unit Price'}</TableCell>
                    <TableCell data-help="sales.invoiceEdit.col.discPct">Disc %</TableCell>
                    <TableCell data-help="sales.invoiceEdit.col.discAmount">Disc amount</TableCell>
                    {showTaxCodes && <TableCell data-help="sales.invoiceEdit.col.taxCode">Tax code</TableCell>}
                    <TableCell data-help="sales.invoiceEdit.col.taxPct">Tax %</TableCell>
                    {isDraft && <TableCell data-help="sales.invoiceEdit.col.revenue">Revenue Account</TableCell>}
                    {isDraft && <TableCell data-help="sales.invoiceEdit.col.segment">Segment</TableCell>}
                    <TableCell align="right" data-help="sales.invoiceEdit.col.net">Net</TableCell>
                    <TableCell align="right" data-help="sales.invoiceEdit.col.tax">Tax</TableCell>
                    {isDraft && <TableCell></TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {lines.map((line, index) => (
                    <TableRow key={line.key}>
                      {isDraft && (
                        <TableCell sx={{ minWidth: 190 }}>
                          <SalesCatalogueSelect data-help="sales.invoiceEdit.col.product"
                            label="Product / service (optional)"
                            value={line.itemId ?? null}
                            valueLabel={line.itemLabel}
                            onChange={(itemId, item) => setLines(prev => prev.map((l, i) => (i !== index ? l : !itemId || !item
                              ? { ...l, itemId: null, itemLabel: undefined, catalogueBdtPrice: null }
                              : {
                                ...l, itemId, itemLabel: `${item.code} - ${item.name}`, description: item.name,
                                unitPrice: item.salePrice != null ? catalogueUnitPrice(item.salePrice, currency, fxRate) : l.unitPrice,
                                catalogueBdtPrice: item.salePrice != null ? item.salePrice : null,   // polish 4
                                revenueAccountId: item.revenueAccountId || l.revenueAccountId,
                              })))}
                          />
                        </TableCell>
                      )}
                      <TableCell>
                        <TextField data-help="sales.invoiceEdit.col.description"
                          size="small"
                          value={line.description}
                          onChange={e => handleLineChange(index, 'description', e.target.value)}
                          placeholder="Description"
                          sx={{ minWidth: 160 }}
                        />
                      </TableCell>
                      <TableCell>
                        {isDraft && !line.challanLinked ? (
                          <TextField data-help="sales.invoiceEdit.col.qty"
                            type="number"
                            size="small"
                            value={Number.isFinite(line.quantity) ? line.quantity : ''}
                            onChange={e => {
                              const v = e.target.value;
                              handleLineChange(index, 'quantity', v === '' ? NaN : Number(v));
                            }}
                            error={!(line.quantity > 0)}
                            inputProps={{ step: 'any', min: 0 }}
                            sx={{ width: 90 }}
                          />
                        ) : (
                          // Restricted: quantity is read-only.
                          <Typography variant="body2">{line.quantity}</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <TextField data-help="sales.invoiceEdit.col.unitPrice"
                          type="number"
                          size="small"
                          value={Number.isFinite(line.unitPrice) ? line.unitPrice : ''}
                          onChange={e => {
                            const v = e.target.value;
                            handleLineChange(index, 'unitPrice', v === '' ? NaN : Number(v));
                          }}
                          error={!(line.unitPrice > 0)}
                          inputProps={{ step: 'any', min: 0 }}
                          sx={{ width: 110 }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField data-help="sales.invoiceEdit.col.discPct"
                          type="number" size="small"
                          inputProps={{ 'aria-label': 'Disc %', step: 'any', min: 0, max: 100 }}
                          value={line.discountPct ?? ''}
                          onChange={e => setLines(prev => prev.map((l, i) => (i !== index ? l : { ...l, ...discountPatch('pct', e.target.value) })))}
                          error={!!discountError(line)}
                          helperText={discountError(line) ?? undefined}
                          sx={{ width: 80 }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField data-help="sales.invoiceEdit.col.discAmount"
                          type="number" size="small"
                          inputProps={{ 'aria-label': 'Disc amount', step: 'any', min: 0 }}
                          value={shownDiscountAmount(line)}
                          onChange={e => setLines(prev => prev.map((l, i) => (i !== index ? l : { ...l, ...discountPatch('amount', e.target.value) })))}
                          sx={{ width: 100 }}
                        />
                      </TableCell>
                      {showTaxCodes && (
                        <TableCell>
                          <TaxCodeSelect data-help="sales.invoiceEdit.col.taxCode"
                            data-testid={`inv-taxcode-${index}`}
                            lookup={taxLookup}
                            side="OUTPUT"
                            value={line.taxCodeId}
                            onChange={(cid, item) => setLines(prev => prev.map((l, i) => (i !== index ? l : {
                              ...l,
                              taxCodeId: cid,
                              taxRatePct: item ? item.effectiveRatePct : l.taxRatePct,
                            })))}
                          />
                        </TableCell>
                      )}
                      <TableCell>
                        <TextField data-help="sales.invoiceEdit.col.taxPct"
                          type="number"
                          size="small"
                          disabled={!!line.taxCodeId}
                          inputProps={{ 'aria-label': 'Tax %', step: 0.01, min: 0 }}
                          value={Number.isFinite(line.taxRatePct) ? line.taxRatePct : ''}
                          onChange={e => {
                            const v = e.target.value;
                            handleLineChange(index, 'taxRatePct', v === '' ? NaN : Number(v));
                          }}
                          sx={{ width: 80 }}
                        />
                      </TableCell>
                      {isDraft && (
                        <TableCell>
                          <Box sx={{ minWidth: 180 }}>
                            <AccountAutocomplete data-help="sales.invoiceEdit.col.revenue"
                              accounts={leafAccounts(revenueAccounts)}
                              value={line.revenueAccountId ?? ''}
                              label="Revenue Account"
                              placeholder="Default account"
                              onChange={accountId => {
                                handleLineChange(index, 'revenueAccountId', accountId || undefined);
                              }}
                            />
                          </Box>
                        </TableCell>
                      )}
                      {isDraft && (
                        <TableCell>
                          <SearchableSelect data-help="sales.invoiceEdit.col.segment"
                            label="Segment"
                            size="small"
                            sx={{ minWidth: 160 }}
                            options={segments}
                            value={line.segmentId ?? ''}
                            onChange={val => handleLineChange(index, 'segmentId', val || undefined)}
                            getOptionLabel={s => `${s.code} — ${s.name}`}
                            placeholder="—"
                          />
                        </TableCell>
                      )}
                      <TableCell align="right">{formatMoney(safeLineNet(line))}</TableCell>
                      <TableCell align="right">{formatMoney(safeLineTax(line))}</TableCell>
                      {isDraft && (
                        <TableCell>
                          <IconButton
                            data-help="sales.invoice.lines.remove"
                            size="small"
                            color="error"
                            onClick={() => handleRemoveLine(index)}
                            disabled={line.challanLinked}
                            aria-label="remove line"
                          >
                            <Delete />
                          </IconButton>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
          </ScrollableTable>
        )}
      </Paper>

      {/* Totals */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 4 }}>
          <Box>
            <Typography variant="body2" color="text.secondary">Subtotal</Typography>
            <Typography variant="body1">{formatMoney(subtotal)}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Tax</Typography>
            <Typography variant="body1">{formatMoney(taxAmount)}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Total</Typography>
            <Typography variant="h6">{formatMoney(total)}</Typography>
          </Box>
        </Box>
      </Paper>

      {/* Actions */}
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Button
          variant="contained"
          data-help="sales.invoiceEdit.save"
          onClick={isDraft ? handleDraftSubmit : handleRestrictedSubmit}
          disabled={isDraft ? draftSubmitDisabled : restrictedSubmitDisabled}
        >
          {saving ? <CircularProgress size={20} /> : 'Save Changes'}
        </Button>
        <Button variant="outlined" data-help="sales.invoiceEdit.cancel" onClick={() => navigate(-1)} disabled={saving}>
          Cancel
        </Button>
      </Box>
    </Box>
  );
};

export default SalesInvoiceEdit;
